import { BACKEND_CONFIG } from '../config/backendConfig';
/**
 * Media Upload Service using Pre-signed URLs
 * Securely uploads media files directly to S3 via pre-signed URLs
 */

import imageCompression from 'browser-image-compression';

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface MediaFile {
  file: File;
  preview: string;
  type: 'image' | 'video' | 'document';
}

export interface UploadResult {
  success: boolean;
  fileUrl?: string;
  error?: string;
}

const BASE_URL = `${BACKEND_CONFIG.baseURL}`;

class MediaUploadService {
  /**
   * Upload a single media file using pre-signed URL
   */
  async uploadMedia(
    mediaFile: MediaFile,
    athleteId: string,
    token: string,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResult> {
    try {
      console.log('📂 Starting upload for:', { name: mediaFile.file.name, type: mediaFile.file.type, size: mediaFile.file.size });

      // Compress image before upload (matching mobile: 2048x2048, quality 0.8)
      let fileToUpload = mediaFile.file;
      if (mediaFile.type === 'image' && mediaFile.file.type.startsWith('image/')) {
        try {
          console.log('🗜️ Compressing image...');
          fileToUpload = await imageCompression(mediaFile.file, {
            maxSizeMB: 10, // Max 10MB after compression
            maxWidthOrHeight: 2048, // Match mobile dimensions
            useWebWorker: true,
            initialQuality: 0.8, // Match mobile quality
          });
          console.log('✅ Image compressed:', (mediaFile.file.size / 1024 / 1024).toFixed(2), 'MB →', (fileToUpload.size / 1024 / 1024).toFixed(2), 'MB');
        } catch (compressionError) {
          console.warn('⚠️ Image compression failed, uploading original:', compressionError);
          fileToUpload = mediaFile.file;
        }
      }

      // Step 1: Get pre-signed URL from backend
      const uploadUrlResponse = await this.getPreSignedUrl(
        athleteId,
        fileToUpload.name,
        fileToUpload.type,
        fileToUpload.size,
        token
      );

      console.log('🔗 Pre-signed URL response:', uploadUrlResponse);

      if (!uploadUrlResponse.success) {
        console.error('❌ Pre-signed URL generation failed:', uploadUrlResponse.error);
        return { success: false, error: uploadUrlResponse.error };
      }

      // Step 2: Upload directly to S3 using pre-signed URL
      console.log('☁️ Starting S3 upload...');
      const uploadResult = await this.uploadToS3(
        fileToUpload,
        uploadUrlResponse.upload_url!,
        fileToUpload.type,
        onProgress
      );

      console.log('📤 S3 upload result:', uploadResult);

      if (uploadResult.success) {
        console.log('✅ Upload successful, file URL:', uploadUrlResponse.file_url);
        return {
          success: true,
          fileUrl: uploadUrlResponse.file_url,
        };
      } else {
        console.error('❌ S3 upload failed:', uploadResult.error);
        return { success: false, error: uploadResult.error };
      }

    } catch (error) {
      console.error('💥 Media upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed'
      };
    }
  }

  /**
   * Upload multiple media files with individual progress tracking
   */
  async uploadMultipleMedia(
    mediaFiles: MediaFile[],
    athleteId: string,
    token: string,
    onProgress?: (index: number, progress: UploadProgress) => void
  ): Promise<UploadResult[]> {
    const uploadPromises = mediaFiles.map(async (file, index) => {
      return this.uploadMedia(
        file,
        athleteId,
        token,
        (progress) => onProgress?.(index, progress)
      );
    });

    return Promise.all(uploadPromises);
  }

  /**
   * Get pre-signed URL from backend
   */
  private async getPreSignedUrl(
    athleteId: string,
    filename: string,
    contentType: string,
    fileSize: number,
    token: string
  ): Promise<{
    success: boolean;
    upload_url?: string;
    file_url?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${BASE_URL}/profiles/media/generate-upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          athlete_id: athleteId,
          filename: filename,
          content_type: contentType,
          file_size: fileSize,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error?.message || error.error || `HTTP ${response.status}: ${error.message || 'Unknown error'}`);
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get upload URL',
      };
    }
  }

  /**
   * Upload file directly to S3 using pre-signed URL
   */
  private async uploadToS3(
    file: File,
    uploadUrl: string,
    contentType: string,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<{ success: boolean; error?: string }> {
    console.log('🔄 Starting S3 upload:', { filename: file.name, contentType, uploadUrl: uploadUrl.substring(0, 100) + '...' });

    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();

      // Progress tracking
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const percentage = Math.round((event.loaded / event.total) * 100);
          console.log(`📊 Upload progress: ${percentage}% (${event.loaded}/${event.total} bytes)`);
          onProgress({
            loaded: event.loaded,
            total: event.total,
            percentage,
          });
        }
      };

      xhr.onload = () => {
        console.log('✅ S3 upload completed with status:', xhr.status);

        if (xhr.status >= 200 && xhr.status < 300) {
          console.log('🎉 S3 upload successful!');
          resolve({ success: true });
        } else {
          console.error('❌ S3 upload failed with status:', xhr.status);
          console.error('📄 Response text:', xhr.responseText);
          resolve({
            success: false,
            error: `Upload failed with status: ${xhr.status} - ${xhr.responseText || 'Unknown error'}`
          });
        }
      };

      xhr.onerror = (event) => {
        console.error('💥 S3 upload network error:', event);
        resolve({ success: false, error: 'Network error during upload' });
      };

      xhr.ontimeout = () => {
        console.error('⏰ S3 upload timeout');
        resolve({ success: false, error: 'Upload timeout' });
      };

      // Configure request for S3 upload (PUT method with file directly)
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.timeout = 300000; // 5 minutes timeout

      console.log('📡 Configured XHR request:', { method: 'PUT', contentType, timeout: '300s' });

      // Start upload
      xhr.send(file);
    });
  }

  /**
   * Validate media file before upload
   */
  validateMediaFile(file: File): { valid: boolean; error?: string } {
    // Check file type
    const allowedTypes = [
      // Images
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      // Videos
      'video/mp4', 'video/mov', 'video/avi', 'video/quicktime',
      // Documents
      'application/pdf', 'text/plain',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ];

    if (!allowedTypes.includes(file.type.toLowerCase())) {
      return { valid: false, error: 'Unsupported file type' };
    }

    // Check file size
    const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
    const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB
    const MAX_DOCUMENT_SIZE = 25 * 1024 * 1024; // 25MB

    if (file.type.startsWith('image/') && file.size > MAX_IMAGE_SIZE) {
      return { valid: false, error: 'Image must be smaller than 10MB' };
    }

    if (file.type.startsWith('video/') && file.size > MAX_VIDEO_SIZE) {
      return { valid: false, error: 'Video must be smaller than 100MB' };
    }

    if (file.type.startsWith('application/') || file.type.startsWith('text/')) {
      if (file.size > MAX_DOCUMENT_SIZE) {
        return { valid: false, error: 'Document must be smaller than 25MB' };
      }
    }

    return { valid: true };
  }
}

export const mediaUploadService = new MediaUploadService();
